import type { IntlShape } from 'react-intl';
import { music24PlanCapabilities, MUSIC24_TIER_RANK, type Music24PlanTier } from 'fym-common/src/license/music24Plans';
import { MUSIC24_GENERAL_PLANS, type Music24Plan } from '../config/music24StripePlans';

/** A reach number for a row or a tile: 412K, 2.8M; below 1,000 the plain number. A missing number is a dash. */
export function formatReach(intl: IntlShape, value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  return Math.abs(value) >= 1000
    ? intl.formatNumber(value, { notation: 'compact', maximumFractionDigits: 1 })
    : intl.formatNumber(value);
}

/** A whole number with group marks (18,420), or a dash. */
export function formatCount(intl: IntlShape, value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : intl.formatNumber(value);
}

/** The 7-day change: +12%, −6% (a real minus), or a dash. */
export function formatChange(intl: IntlShape, value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  const text = intl.formatNumber(Math.abs(value) / 100, { style: 'percent', maximumFractionDigits: 1 });
  if (value > 0) {
    return `+${text}`;
  }
  return value < 0 ? `−${text}` : text;
}

/** The tone of a change for its colour. */
export function changeTone(value: number | null | undefined): 'up' | 'down' | 'flat' {
  if (value === null || value === undefined || value === 0) {
    return 'flat';
  }
  return value > 0 ? 'up' : 'down';
}

/** Hours and minutes until `resetsAt` (never below 0). */
export function timeUntil(resetsAt: string, now: number = Date.now()): { hours: number; minutes: number } {
  const totalMinutes = Math.max(0, Math.round((Date.parse(resetsAt) - now) / 60000));
  return { hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60 };
}

/** The sold plans above `plan` that give more browse credits, cheapest first (design b5 upgrade path). */
export function plansWithMoreCredits(plan: Music24PlanTier): Music24Plan[] {
  const current = music24PlanCapabilities[plan].dailyBrowseCredits;
  return MUSIC24_GENERAL_PLANS.filter(
    (candidate) =>
      MUSIC24_TIER_RANK[candidate.tier] > MUSIC24_TIER_RANK[plan] &&
      music24PlanCapabilities[candidate.tier].dailyBrowseCredits > current
  );
}

/** The monthly price in whole dollars. */
export function monthlyDollars(plan: Music24Plan): number {
  return Math.round(plan.prices.monthly.amount / 100);
}
